import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RecipeService } from '@services/recipe/recipe.service';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

export interface SEORecipe {
  seoTitle: string;
  seoUrl: string;
}

@Component({
  selector: 'app-seo',
  templateUrl: './seo.component.html',
  styleUrls: ['./seo.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SeoComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, TranslateModule, MatTooltipModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeoComponent implements OnInit, OnDestroy {
  @Input() recipeOriginal: RecipeLite;
  @Input() isSeoURLDisabled$: BehaviorSubject<boolean>;
  @Output() updateRecipe = new EventEmitter<Partial<RecipeLite>>();
  @Output() formDirtyState = new EventEmitter<boolean>();

  seoForm: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private recipeService: RecipeService,
  ) {
    this.seoForm = this.fb.group<SEORecipe>({
      seoTitle: '',
      seoUrl: '',
    });
  }

  ngOnInit(): void {
    if (this.recipeOriginal) {
      this.seoForm.patchValue({
        seoTitle: this.recipeOriginal.seoTitle ?? '',
        seoUrl: this.recipeOriginal.seoUrl ?? '',
      });
    }

    this.isSeoURLDisabled$
      .pipe(
        tap((isDisabled) => this.setSeoUrlEditable(isDisabled)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.seoForm.valueChanges.subscribe(() => {
      this.formDirtyState.emit(this.seoForm.dirty);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSeoUrlEditable(isDisabled: boolean): void {
    if (isDisabled) {
      this.seoForm.controls.seoUrl.disable();
    } else {
      this.seoForm.controls.seoUrl.enable();
    }
  }

  save(): void {
    this.updateRecipe.emit(this.recipeService.convertSeoFormToRecipeLite(this.recipeOriginal, this.seoForm.value));
    this.seoForm.markAsPristine();
    this.formDirtyState.emit(this.seoForm.dirty);
  }

  reset(): void {
    if (this.recipeOriginal) {
      this.seoForm.markAsPristine();
      this.seoForm.patchValue({
        seoTitle: this.recipeOriginal.seoTitle ?? '',
        seoUrl: this.recipeOriginal.seoUrl ?? '',
      });
      this.formDirtyState.emit(this.seoForm.dirty);
    }
  }
}
